import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Phone, Navigation, Route, Clock, MapPin } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { Panel, Empty, SelectField, inputCls } from "@/components/Bits";
import { StatusBadge } from "@/config/os";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/lib/useFetch";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const shift = (s, n) => { const d = new Date(`${s}T12:00:00`); d.setDate(d.getDate() + n); return iso(d); };
const dest = (f) => (f?.latitude != null && f?.longitude != null ? `${f.latitude},${f.longitude}` : [f?.endereco, f?.municipio, f?.estado].filter(Boolean).join(", "));
const phoneOf = (o) => (o.fazenda?.telefone || o.cliente?.whatsapp || o.cliente?.telefone || "").replace(/\D/g, "");

const Visit = ({ o, i }) => {
  const d = dest(o.fazenda);
  const tel = phoneOf(o);
  return (
    <div className="bg-white rounded-2xl border border-[#E2DDD3] p-4 fade-up" style={{ animationDelay: `${i * 50}ms` }} data-testid={`agenda-item-${o.os_id}`}>
      <div className="flex items-start gap-4">
        <div className="shrink-0 w-16 text-center">
          <p className="font-display text-2xl font-extrabold text-[#1E4D2B]" data-testid={`agenda-hora-${o.os_id}`}>{o.horario || "--:--"}</p>
          <p className="text-[10px] uppercase font-bold text-[#5C6B66]">Parada {i + 1}</p>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2"><Link to={`/ordens-servico/${o.os_id}`} className="font-semibold hover:underline">{o.numero}</Link><StatusBadge status={o.status} /></div>
          <p className="font-semibold mt-1 truncate">{o.fazenda?.nome} · {o.cliente?.nome}</p>
          <p className="text-sm text-[#5C6B66] flex items-center gap-1"><MapPin className="h-4 w-4 shrink-0" /><span className="truncate">{[o.fazenda?.municipio, o.fazenda?.estado].filter(Boolean).join(" / ") || "Sem localização"}</span></p>
          <p className="text-sm mt-1">{o.categoria_nome || o.servico_solicitado}</p>
          {o.tecnico_nome && <p className="text-xs text-[#5C6B66] mt-1">Técnico: {o.tecnico_nome}</p>}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 mt-4">
        <a href={d ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(d)}&travelmode=driving` : undefined} target="_blank" rel="noreferrer"
          aria-disabled={!d} data-testid={`agenda-rota-${o.os_id}`}
          className={`h-14 rounded-xl font-semibold flex items-center justify-center gap-2 ${d ? "bg-[#1E4D2B] text-white hover:bg-[#163A20]" : "bg-[#ECEAE4] text-[#9A9A9A] pointer-events-none"}`}>
          <Navigation className="h-5 w-5" /> Rota
        </a>
        <a href={tel ? `tel:${tel}` : undefined} aria-disabled={!tel} data-testid={`agenda-ligar-${o.os_id}`}
          className={`h-14 rounded-xl font-semibold flex items-center justify-center gap-2 ${tel ? "bg-[#C86D51] text-white hover:bg-[#B05A40]" : "bg-[#ECEAE4] text-[#9A9A9A] pointer-events-none"}`}>
          <Phone className="h-5 w-5" /> Ligar
        </a>
      </div>
    </div>
  );
};

const dayRoute = (items) => {
  const pts = [...new Set(items.filter((o) => o.status !== "finalizada").map((o) => dest(o.fazenda)).filter(Boolean))];
  if (!pts.length) return null;
  const last = pts.pop();
  const wp = pts.length ? `&waypoints=${encodeURIComponent(pts.join("|"))}` : "";
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(last)}${wp}&travelmode=driving`;
};

const Group = ({ name, items }) => {
  const route = dayRoute(items);
  return (
    <Panel title={`${name} · ${items.length} visita(s)`} testId="agenda-group"
      action={route && <a href={route} target="_blank" rel="noreferrer" data-testid="agenda-rota-dia"
        className="h-12 px-4 rounded-xl border-2 border-[#1E4D2B] text-[#1E4D2B] font-semibold flex items-center gap-2 hover:bg-[#E8F5E9]"><Route className="h-5 w-5" /> Rota do dia</a>}>
      <div className="space-y-3">{items.map((o, i) => <Visit key={o.os_id} o={o} i={i} />)}</div>
    </Panel>
  );
};

export default function AgendaPage() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [data, setData] = useState(iso(new Date()));
  const [tec, setTec] = useState("");
  const { data: equipe } = useFetch(isAdmin ? "/ordens/equipe" : null);
  const { data: items } = useFetch("/agenda", { data, tecnico_id: tec || undefined });
  const groups = {};
  (items || []).forEach((o) => { const k = o.tecnico_nome || "Sem técnico"; (groups[k] = groups[k] || []).push(o); });
  const label = new Date(`${data}T12:00:00`).toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long" });

  return (
    <div data-testid="agenda-page">
      <PageHeader eyebrow="Campo" title="Agenda" subtitle={isAdmin ? "Visitas do dia por técnico." : "Suas visitas do dia."} />
      <div className="flex flex-col lg:flex-row gap-3 mb-6">
        <div className="flex gap-2 items-center flex-1">
          <button onClick={() => setData(shift(data, -1))} className="h-14 w-14 shrink-0 rounded-xl bg-white border border-[#E2DDD3] flex items-center justify-center" aria-label="Dia anterior" data-testid="agenda-prev"><ChevronLeft className="h-6 w-6" /></button>
          <input type="date" value={data} onChange={(e) => e.target.value && setData(e.target.value)} className={`${inputCls} bg-white`} data-testid="agenda-date" />
          <button onClick={() => setData(shift(data, 1))} className="h-14 w-14 shrink-0 rounded-xl bg-white border border-[#E2DDD3] flex items-center justify-center" aria-label="Próximo dia" data-testid="agenda-next"><ChevronRight className="h-6 w-6" /></button>
          <button onClick={() => setData(iso(new Date()))} className="h-14 px-4 shrink-0 rounded-xl bg-[#1E4D2B] text-white font-semibold" data-testid="agenda-today">Hoje</button>
        </div>
        {isAdmin && <div className="lg:w-72"><SelectField label="" id="ag-tec" placeholder="Todos os técnicos" value={tec} onChange={(e) => setTec(e.target.value)}
          options={(equipe || []).map((u) => ({ value: u.user_id, label: u.name }))} data-testid="agenda-tecnico-select" /></div>}
      </div>
      <p className="mb-4 font-semibold capitalize flex items-center gap-2" data-testid="agenda-date-label"><Clock className="h-5 w-5 text-[#C86D51]" /> {label}</p>
      {items?.length === 0 && <Empty testId="agenda-empty">Nenhuma OS agendada para este dia.</Empty>}
      <div className="space-y-5">{Object.entries(groups).map(([name, list]) => <Group key={name} name={name} items={list} />)}</div>
    </div>
  );
}
